import {
  Box,
  Button,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { getUser } from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance'; 

const Enabled = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');

  // 상태 관리
  const [gameProviders, setGameProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 초기 데이터 로딩
  useEffect(() => {
    fetchProviders();
  }, []);

  // 벤더 목록 가져오기
  const fetchProviders = async () => {
    setLoading(true);
    try {
      // 사용자 정보 가져오기
      const user = getUser();
      console.log('로그인 사용자 정보:', user);
      
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }
      
      // 사용자 등급에 따라 다른 API 호출
      let response;
      if (user.grade === 'admin') {
        // 관리자용 API
        response = await axiosInstance.get('/games/vendor');
      } else {
        // 에이전트용 API - agent_id 파라미터 전달
        response = await axiosInstance.get('/games/agent/company', {
          params: { agent_id: user.id }
        });
      }
      
      if (response.data && response.data.success) {
        const vendorsData = response.data.vendors;
        console.log('API에서 로드된 원본 데이터:', vendorsData);
        
        // 각 게임사의 데이터를 형식화
        const formattedProviders = Object.keys(vendorsData).map((providerName, index) => {
          // 해당 공급사의 첫 번째 항목 가져오기 (그룹화된 결과에서는 배열의 첫 번째 항목만 사용)
          const providerData = vendorsData[providerName][0];
          
          return {
            id: providerData.id, // 고유 ID 사용
            name: providerName,
            // use가 1이면 활성화(true), 0이면 비활성화(false)
            enabled: providerData.use === 1 || providerData.use === "1",
            // 추가 데이터가 필요하면 포함
            updatedAt: providerData.updated_at
          };
        });
        
        setGameProviders(formattedProviders);
      } else {
        throw new Error('유효한 데이터가 반환되지 않았습니다.');
      }
    } catch (err) {
      console.error('게임사 목록 가져오기 실패:', err);
      setError('게임사 목록을 불러오는데 실패했습니다: ' + (err.message || '알 수 없는 오류'));
      setGameProviders([]); // 오류 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  };

  // 설정 저장하기
  const saveSettings = async () => {
    setLoading(true);
    try {
      // 사용자 정보 가져오기
      const user = getUser();
      console.log('로그인 사용자 정보:', user);
      
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      // 사용자 등급 확인
      const isAdmin = user.grade === 'admin';
      
      if (isAdmin) {
        // 관리자용 형식: { providers: [{name, use}] }
        const providerData = gameProviders.map(provider => ({
          name: provider.name,
          use: provider.enabled ? "1" : "0"
        }));
        
        const response = await axiosInstance.post(
          '/games/update-vendor',
          { providers: providerData }
        );
        
        console.log('관리자 API 응답:', response.data);
        setSuccess('게임사 설정이 성공적으로 저장되었습니다.');
      } else {
        // 에이전트용 - 개별 게임사 상태를 각각 처리 (수정된 부분)
        const updatePromises = gameProviders.map(provider => {
          return axiosInstance.post('/games/use/company', {
            providers: [provider.name],
            game_company: provider.name,
            status: provider.enabled ? "1" : "0"
          });
        });
        
        // 모든 요청 완료 대기
        await Promise.all(updatePromises);
        
        console.log('에이전트 API 요청 완료: 각 게임사별 개별 처리됨');
        setSuccess('게임사 설정이 성공적으로 저장되었습니다.');
      }
      
      // 저장 후 데이터 다시 로드
      fetchProviders();
    } catch (err) {
      console.error('설정 저장 실패:', err.response?.data || err.message);
      setError('설정 저장에 실패했습니다: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Switch 상태 변경 함수
  const toggleSwitch = id => {
    setGameProviders(prev =>
      prev.map(provider =>
        provider.id === id ? { ...provider, enabled: !provider.enabled } : provider,
      ),
    );
  };

  // 전체 사용 활성화/비활성화
  const setAllEnabled = enabled => {
    setGameProviders(prev => prev.map(provider => ({ ...provider, enabled })));
  };

  // 알림 닫기
  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  // 다크 모드 스타일 변수
  const darkModeStyles = {
    container: {
      p: 2, 
      background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
      minHeight: '100vh'
    },
    paper: {
      p: isNonMobile ? 3 : 2,
      background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(8px)',
    },
    title: {
      color: '#fff',
      fontSize: '28px',
      fontWeight: 'bold',
      mb: 4,
      letterSpacing: '0.5px',
      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
    },
    buttonContainerStyles: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      mb: 4,
      flexWrap: 'wrap',
    },
    buttonGroupStyles: {
      display: 'flex', 
      gap: 2
    },
    enableAllButton: {
      backgroundColor: '#4ade80',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 20px',
      boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#43a047',
        boxShadow: '0 6px 12px rgba(67, 160, 71, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    disableAllButton: {
      backgroundColor: '#f87171',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 20px',
      boxShadow: '0 4px 10px rgba(244, 67, 54, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#e53935',
        boxShadow: '0 6px 12px rgba(229, 57, 53, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    saveButton: {
      backgroundColor: '#60a5fa',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 24px',
      boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      minWidth: '100px',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#3b82f6',
        boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    tableContainer: {
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    tableHeadCell: {
      backgroundColor: 'rgba(45, 45, 65, 0.9)',
      color: '#fff',
      paddingTop: '16px',
      paddingBottom: '16px',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    tableCell: {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      padding: '16px',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
      transition: 'background-color 0.2s'
    },
    switch: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#4ade80',
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#4ade80',
      },
      '& .MuiSwitch-switchBase': {
        '&:hover': {
          backgroundColor: 'rgba(74, 222, 128, 0.08)',
        }
      },
      '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      },
      '& .MuiSwitch-track': {
        borderRadius: 20,
      },
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      my: 5,
      height: '200px'
    },
    noDataText: {
      color: 'rgba(255, 255, 255, 0.7)',
      textAlign: 'center',
      my: 4,
      fontStyle: 'italic'
    }
  };

  return (
    <Box sx={darkModeStyles.container}>
      {/* 에러 메시지 */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* 성공 메시지 */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Paper sx={darkModeStyles.paper}>
        <Typography variant="h4" sx={darkModeStyles.title}>
          게임사 사용 여부
        </Typography>
        
        <Box sx={darkModeStyles.buttonContainerStyles}>
          <Box sx={darkModeStyles.buttonGroupStyles}>
            <Button
              variant="contained"
              onClick={() => setAllEnabled(true)}
              disabled={loading}
              sx={darkModeStyles.enableAllButton}
              startIcon={loading ? null : <CircleCheckIcon />}
            >
              게임사 전체 사용
            </Button>

            <Button
              variant="contained"
              onClick={() => setAllEnabled(false)}
              disabled={loading}
              sx={darkModeStyles.disableAllButton}
              startIcon={loading ? null : <CircleXIcon />}
            >
              게임사 전체 미사용
            </Button>
          </Box>
          
          <Button
            variant="contained"
            onClick={saveSettings}
            disabled={loading}
            sx={darkModeStyles.saveButton}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '저장'}
          </Button>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box sx={darkModeStyles.loadingContainer}>
            <CircularProgress sx={{ color: '#60a5fa' }} />
          </Box>
        )}

        {/* 게임사 테이블 */}
        {!loading && gameProviders.length === 0 ? (
          <Typography sx={darkModeStyles.noDataText}>
            표시할 게임사가 없습니다.
          </Typography>
        ) : (
          <TableContainer sx={darkModeStyles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={darkModeStyles.tableHeadCell}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      게임사 이름
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={darkModeStyles.tableHeadCell}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      사용 여부
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  gameProviders.map(provider => (
                    <TableRow key={provider.id} sx={darkModeStyles.tableRow}>
                      <TableCell sx={darkModeStyles.tableCell}>
                        <Typography sx={{ fontWeight: 600 }}>{provider.name}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={darkModeStyles.tableCell}>
                        <Switch
                          checked={provider.enabled}
                          onChange={() => toggleSwitch(provider.id)}
                          color="primary"
                          sx={darkModeStyles.switch}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// 아이콘 컴포넌트
const CircleCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="m9 12 2 2 4-4"></path>
  </svg>
);

const CircleXIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="m15 9-6 6"></path>
    <path d="m9 9 6 6"></path>
  </svg>
);

export default Enabled;